import { useContext, useState } from 'react';
import { TenantSelector } from '../../../components/tenant-selector/TenantSelector';
import { toTargetedTenantsFilter } from '../engage-automations-tab/templates.types';
import { useSyncAutomationTemplates, useUpdateAutomationTemplate } from '../useAutomationTemplates';
import { AutomationEditor } from './AutomationEditor';
import { AutomationEditorContext } from './AutomationEditorContext';
import { getAutomationTemplateSaveButtonLabel } from './automationTemplateSaveButtonLabel';
import styles from './EditAutomationTemplate.module.css';
import { ReorderWarningDialog } from './ReorderWarningDialog';
import { SettingsControl } from './settings-controls';
import { TenantSelectionControl } from './tenant-selection-control';
import { useAutomationTemplateTenantSelection } from './useAutomationTemplateTenantSelection';
import { useSyncStatusHandshakeLock } from './useSyncStatusHandshakeLock';

const { headerContent, syncButton, reorderNotification } = styles;

export function EditAutomationTemplateContent() {
  const { editor } = useContext(AutomationEditorContext)!;
  const { state, markClean, template, undoReorder } = editor;
  const updateMutation = useUpdateAutomationTemplate();
  const syncMutation = useSyncAutomationTemplates();
  const [showReorderWarning, setShowReorderWarning] = useState(false);

  const { tenantSelection } = state;

  const {
    conflicts,
    isValidating,
    tenantRowData,
    syncingTenantCount,
    jobSyncActive,
    isLoading: isLoadingTenants,
  } = useAutomationTemplateTenantSelection({
    templateId: template.id,
    tenantSelectionType: tenantSelection.selectionType,
    selectedTenantIds: tenantSelection.selectedTenantIds,
  });

  const syncStatusHandshakeLock = useSyncStatusHandshakeLock(jobSyncActive, {
    isPending: syncMutation.isPending,
    isError: syncMutation.isError,
    isSuccess: syncMutation.isSuccess,
  });

  const isSyncing =
    jobSyncActive || syncMutation.isPending || updateMutation.isPending || syncStatusHandshakeLock;

  const { localContents, hasReorderedOrAdded, hasStepsNeedingSetup, isDirty } = state;
  const { description, name } = localContents;

  const handleSave = () => {
    const selectionTypeAtSave = tenantSelection.selectionType;
    const selectedIdsAtSave = tenantSelection.selectedTenantIds;

    updateMutation.mutate(
      {
        id: template.id,
        params: {
          contents: localContents,
          targetedTenantsFilter: toTargetedTenantsFilter(selectionTypeAtSave, selectedIdsAtSave),
        },
      },
      {
        onSuccess: () => {
          markClean();
          syncMutation.mutate({ templateId: template.id, silent: true });
        },
      },
    );
  };

  const handleSaveClick = hasReorderedOrAdded ? () => setShowReorderWarning(true) : handleSave;

  const saveDisabled =
    !isDirty ||
    hasStepsNeedingSetup ||
    isValidating ||
    updateMutation.isPending ||
    syncMutation.isPending ||
    isSyncing;

  let saveTitle: string | undefined;
  if (hasStepsNeedingSetup) {
    saveTitle = 'Please configure all steps before saving';
  } else if (isValidating) {
    saveTitle = 'Validating tenant segment compatibility…';
  } else if (isSyncing) {
    saveTitle = 'Sync in progress';
  } else if (saveDisabled && !isDirty) {
    saveTitle = 'No changes to save';
  }

  const saveButtonLabel = getAutomationTemplateSaveButtonLabel({
    isUpdatePending: updateMutation.isPending,
    jobSyncActive,
    syncingTenantCount,
    isSyncMutationPending: syncMutation.isPending,
  });

  return (
    <>
      <header className={headerContent}>
        <hgroup>
          <h1>{name}</h1>
          <p>{description}</p>
          {hasReorderedOrAdded && (
            <div className={reorderNotification}>
              <span>Automation steps have been added, deleted or re-ordered.</span>
              <button
                type="button"
                onClick={undoReorder}
                aria-label="Undo reorder"
                disabled={isSyncing}
                title={isSyncing ? 'Not available while sync is in progress' : undefined}>
                Undo?
              </button>
            </div>
          )}
        </hgroup>
        <aside>
          <TenantSelectionControl
            disabled={isSyncing}
            tenantRowData={tenantRowData}
            conflicts={conflicts}
            isValidating={isValidating}
            isLoading={isLoadingTenants}
          />
          <SettingsControl disabled={isSyncing} />
          <button
            className={syncButton}
            type="button"
            onClick={handleSaveClick}
            disabled={saveDisabled}
            title={saveTitle}>
            {saveButtonLabel}
          </button>
        </aside>
      </header>

      {isSyncing ? (
        <div className={styles.syncingBody}>
          <p className={styles.syncingMessage}>Cannot edit template while syncing</p>
          <TenantSelector
            tenantSelectionType={tenantSelection.selectionType}
            selectedTenantIds={tenantSelection.selectedTenantIds}
            onChange={() => {}}
            allTenantsSelectionDescription=""
            selectedTenantsSelectionDescription=""
            disabled
            tenantRowData={tenantRowData}
            listOnly
          />
        </div>
      ) : (
        <div className="mt-4">
          <AutomationEditor />
        </div>
      )}

      <ReorderWarningDialog
        open={showReorderWarning}
        onClose={() => setShowReorderWarning(false)}
        onConfirm={handleSave}
      />
    </>
  );
}
